import React from "react"
import { Link } from "react-router-dom"
import type { TFunction } from "i18next"
import { Routes } from "../routes"
import { formatNumber } from "../utils/formatDate"
import { performAppLogout } from "../utils/performAppLogout"

function initialsFromName(name: string | undefined): string {
    if (!name?.trim()) return "?"
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export interface UserProfileStickyProps {
    t: TFunction
    loadcompany: boolean
    fullname: string
    email: string
    role: string | undefined
    planLabel: string
    creditsAvailable: number | null
    creditsLoading: boolean
    isAdmin: boolean
    /** Ex. page contributeur sans modal #inviteOwner */
    showInviteOwnerInMenu?: boolean
    /** Changement de compagnie — réservé à la sidebar Insights par défaut */
    showChangeCompanyInMenu?: boolean
}

function UserProfileSticky(props: UserProfileStickyProps) {
    const {
        t,
        loadcompany,
        fullname,
        email,
        role,
        planLabel,
        creditsAvailable,
        creditsLoading,
        isAdmin,
        showInviteOwnerInMenu = true,
        showChangeCompanyInMenu = false,
    } = props

    const showNavSection =
        showInviteOwnerInMenu || loadcompany || showChangeCompanyInMenu || isAdmin

    const roleBadge =
        role === "ADMIN"
            ? t("layout.header.roleAdmin")
            : role
              ? t("layout.header.roleUser")
              : ""

    return (
        <div className="lp-user-profile-sticky">
            <div className="dropdown dropup lp-user-menu-wrap">
                <button
                    type="button"
                    className="dropdown-toggle lp-user-trigger"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    id="lpUserMenuButton"
                >
                    <div className="lp-user-avatar">{initialsFromName(fullname)}</div>
                    <div className="lp-user-info">
                        <div className="lp-user-name">{fullname}</div>
                        <div className="lp-user-plan">
                            {planLabel}
                            {creditsAvailable !== null && (
                                <>
                                    {" · "}
                                    {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                    {t("dashboard.totalCredits")}
                                </>
                            )}
                        </div>
                    </div>
                    <span className="lp-user-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                            <path
                                d="M4 10L8 6L12 10"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </button>

                <div
                    className="dropdown-menu lp-user-menu-panel"
                    aria-labelledby="lpUserMenuButton"
                >
                    <div className="lp-user-menu-profile">
                        <div className="lp-user-menu-avatar">{initialsFromName(fullname)}</div>
                        <div className="lp-user-menu-profile-text">
                            <div className="lp-user-menu-profile-name">{fullname}</div>
                            <div className="lp-user-menu-profile-email">{email}</div>
                            {roleBadge ? (
                                <span className="lp-user-menu-role">
                                    <i className="fa-solid fa-shield-halved lp-user-menu-role-icon" aria-hidden />
                                    {roleBadge}
                                </span>
                            ) : null}
                            {creditsAvailable !== null && (
                                <div className="lp-user-menu-profile-meta">
                                    {planLabel}
                                    {" · "}
                                    {isAdmin ? (
                                        <Link
                                            className="lp-user-menu-meta-link"
                                            to={Routes.DASHBOARDS}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                            {t("dashboard.totalCredits")}
                                        </Link>
                                    ) : (
                                        <>
                                            {creditsLoading ? "…" : formatNumber(creditsAvailable)}{" "}
                                            {t("dashboard.totalCredits")}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {showNavSection ? (
                        <>
                            <div className="lp-user-menu-divider" role="separator" />

                            <nav className="lp-user-menu-nav" aria-label={t("layout.header.userMenuNav")}>
                                {showInviteOwnerInMenu ? (
                                    <a
                                        className="lp-user-menu-item"
                                        href="#/"
                                        data-bs-toggle="modal"
                                        data-bs-target="#inviteOwner"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="fa-solid fa-user-plus lp-user-menu-item-icon" aria-hidden />
                                        <span>{t("navigation.inviteOwner")}</span>
                                    </a>
                                ) : null}
                                {loadcompany ? (
                                    <a
                                        className="lp-user-menu-item"
                                        href="#/"
                                        data-bs-toggle="modal"
                                        data-bs-target="#requestApproval"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="fa-solid fa-clipboard-check lp-user-menu-item-icon" aria-hidden />
                                        <span>{t("layout.header.approvalRequests")}</span>
                                    </a>
                                ) : null}
                                {showChangeCompanyInMenu ? (
                                    <a
                                        className="lp-user-menu-item"
                                        href="#/"
                                        data-bs-toggle="modal"
                                        data-bs-target="#changeCompany"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <i className="fa-solid fa-building lp-user-menu-item-icon" aria-hidden />
                                        <span>{t("layout.header.company")}</span>
                                    </a>
                                ) : null}
                                {isAdmin && (
                                    <Link className="lp-user-menu-item" to={Routes.DASHBOARDS} onClick={(e) => e.stopPropagation()}>
                                        <i className="fa-solid fa-chart-line lp-user-menu-item-icon" aria-hidden />
                                        <span>{t("dashboard.dashboard")}</span>
                                    </Link>
                                )}
                            </nav>
                        </>
                    ) : null}

                    <div className="lp-user-menu-divider" role="separator" />

                    <nav className="lp-user-menu-nav" aria-label={t('layout.header.userMenuNav')}>
                        <a
                            className="lp-user-menu-item"
                            href="#/"
                            data-bs-toggle="modal"
                            data-bs-target="#editUserPhone"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <i className="fa-brands fa-whatsapp lp-user-menu-item-icon" aria-hidden />
                            <span>{t('navigation.editPhone')}</span>
                        </a>
                    </nav>

                    <div className="lp-user-menu-divider" role="separator" />

                    <div className="lp-user-menu-footer">
                        <button
                            type="button"
                            className="lp-user-menu-logout"
                            onClick={() => {
                                void performAppLogout()
                            }}
                        >
                            <i className="fa-solid fa-arrow-right-from-bracket lp-user-menu-item-icon" aria-hidden />
                            <span>{t("navigation.logOut")}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserProfileSticky
